import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {ButtonDocument, StaticHeaderSliceV2} from 'prismicio-types'
import {ButtonV2} from 'src/common/ButtonV2'
import {BASE_URL} from 'src/common/constants'
import {EVENTS, getButtonsData, track} from 'src/common/helpers'
import {useDynamicBackground} from 'src/common/hooks'
import {Icon} from 'src/common/Icon'

/**
 * Props for `StaticHeader`.
 */
export type StaticHeaderDefaultProps = {
  slice: StaticHeaderSliceV2
  pageUid: string
  buttonsData?: ButtonDocument[]
}

/**
 * Component for "StaticHeader" Slices.
 */
export const StaticHeaderV2 = ({slice: {primary}, pageUid, buttonsData}: StaticHeaderDefaultProps): JSX.Element => {
  const {linkResolver} = useLinkResolver()

  const {primaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: primary.primary_button,
  })

  const {isBackgroundDark} = useDynamicBackground({initialState: true})

  return (
    <header className="fixed top-0 z-20 w-full px-5 pt-3 md:max-lg:px-10 xl:px-0">
      <div className="relative mx-auto flex h-[48px] max-w-[1200px] items-center justify-between bg-moss-white/[50%] py-2 pl-4 pr-2 backdrop-blur-[15px]">
        <div
          className={cn('pointer-events-none absolute inset-0 overflow-hidden rounded-[4px] border bg-transparent', {
            'border-moss-white/[10%]': isBackgroundDark,
            'border-black/[10%]': !isBackgroundDark,
          })}
        />
        {true ? (
          <Link
            className="flex items-center"
            href={linkResolver({link_type: 'Web', url: BASE_URL})}
            onClick={() =>
              track({
                event: EVENTS.CLICK,
                location: 'nav-bar',
                dataType: 'logo-link',
                mktgId: 'logo',
                pageName: pageUid,
              })
            }
          >
            <Icon className="h-[22px] w-[91px]" name="moss-logo-v2" />
          </Link>
        ) : (
          <Icon className="h-[22px] w-[91px]" name="moss-logo-v2" />
        )}
        {primaryButtonData && (
          <ButtonV2
            className="!max-h-[32px] !px-3 !py-2 uppercase max-lg:px-3 max-lg:text-[11px]"
            size="small"
            data-page-location="nav-bar"
            variant="primary"
            href={linkResolver(primaryButtonData.link, 'primary-button-header')}
            trackingData={{
              location: 'nav-bar',
              dataType: 'primary-button',
              mktgId: primaryButtonData?.marketing_id || '',
              pageName: pageUid,
            }}
            id="header-primary-button"
          >
            {primaryButtonData.label}
          </ButtonV2>
        )}
      </div>
    </header>
  )
}
